import IconButton from "@Components/shared/IconButton";
import {
    PhotoCamera,
    Refresh,
    Fullscreen,
    FullscreenExit,
    Devices,
    Videocam,
    Stop,
    Download as DownloadIcon,
    ZoomIn,
    ZoomOut,
    RemoveRedEye as Eye,
    VisibilityOff as EyeOff,
    PanTool as Hand,
    PanTool as HandOff,
    Menu,
    Close as X,
} from "@mui/icons-material";
import { Card, Col, Row, Select, Slider, Space } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";

interface Device {
    deviceId: string;
    label: string;
}

interface Position {
    x: number;
    y: number;
}

const WebcamViewer: React.FC = () => {
    const webcamRef = useRef<Webcam>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const viewportRef = useRef<HTMLDivElement>(null);
    const [photo, setPhoto] = useState<string | null>(null);
    const [isCameraActive, setIsCameraActive] = useState(true);
    const [devices, setDevices] = useState<Device[]>([]);
    const [selectedDevice, setSelectedDevice] = useState<string>("");
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
    const [endoscopeMask, setEndoscopeMask] = useState(false);
    const [maskSize, setMaskSize] = useState(30);
    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });
    const [handShake, setHandShake] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        const getDevices = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices
                    .filter((device) => device.kind === "videoinput")
                    .map((device) => ({
                        deviceId: device.deviceId,
                        label: device.label || `Camera ${devices.indexOf(device) + 1}`,
                    }));
                setDevices(videoDevices);
                if (videoDevices.length > 0) {
                    setSelectedDevice(videoDevices[0].deviceId);
                }
            } catch (error) {
                console.error("Error accessing camera devices:", error);
            }
        };

        getDevices();
    }, []);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (zoom > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y,
            });
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging && zoom > 1) {
            const maxOffset = (zoom - 1) * 200;
            const newX = e.clientX - dragStart.x;
            const newY = e.clientY - dragStart.y;

            setPosition({
                x: Math.max(Math.min(newX, maxOffset), -maxOffset),
                y: Math.max(Math.min(newY, maxOffset), -maxOffset),
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const delta = -Math.sign(e.deltaY) * 0.1;
        const newZoom = Math.max(1, Math.min(5, zoom + delta));
        setZoom(newZoom);

        if (newZoom === 1) {
            setPosition({ x: 0, y: 0 });
        }
    };

    useEffect(() => {
        const handleGlobalMouseUp = () => {
            setIsDragging(false);
        };

        window.addEventListener("mouseup", handleGlobalMouseUp);
        return () => {
            window.removeEventListener("mouseup", handleGlobalMouseUp);
        };
    }, []);

    const capture = useCallback(() => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
            setPhoto(imageSrc);
            setIsCameraActive(false);
        }
    }, [webcamRef]);

    const retake = useCallback(() => {
        setPhoto(null);
        setIsCameraActive(true);
        setRecordedChunks([]);
    }, []);

    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    }, []);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
        };
    }, []);

    const handleStartRecording = useCallback(() => {
        if (webcamRef.current?.stream) {
            setRecordedChunks([]);
            mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
                mimeType: "video/webm",
            });
            mediaRecorderRef.current.addEventListener("dataavailable", ({ data }) => {
                if (data.size > 0) {
                    setRecordedChunks((prev) => [...prev, data]);
                }
            });
            mediaRecorderRef.current.start();
            setIsRecording(true);
        }
    }, [webcamRef, setRecordedChunks]);

    const handleStopRecording = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            setIsCameraActive(false);
        }
    }, [mediaRecorderRef]);

    const handleDownload = useCallback(() => {
        if (recordedChunks.length > 0) {
            const blob = new Blob(recordedChunks, {
                type: "video/webm",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            document.body.appendChild(a);
            a.style.display = "none";
            a.href = url;
            a.download = `recording-${new Date().toISOString()}.webm`;
            a.click();
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
        }
    }, [recordedChunks]);

    const handleZoomIn = () => {
        setZoom((prev) => Math.min(5, prev + 0.1));
    };

    const handleZoomOut = () => {
        const newZoom = Math.max(1, zoom - 0.1);
        setZoom(newZoom);
        if (newZoom === 1) {
            setPosition({ x: 0, y: 0 });
        }
    };

    const videoConstraints = {
        width: 1280,
        height: 720,
        deviceId: selectedDevice,
    };

    return (
        <Card
            ref={containerRef}
            className={isFullscreen ? "" : "card-no-padding card-no-border"}
            style={isFullscreen ? { height: "100vh", position: "fixed", inset: 0, zIndex: 40 } : { padding: 0 }}
        >
            {/* Controls Panel - Normal View */}
            {!isFullscreen && (
                <div style={{ marginBottom: 20 }}>
                    <Row gutter={[24, 16]}>
                        {/* Camera Settings */}
                        <Col span={8}>
                            <h3 style={{ marginBottom: 8 }}>Camera Settings</h3>
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <div>
                                    <label style={{ display: "block", marginBottom: 4 }}>Select Camera</label>
                                    <Space>
                                        <Devices style={{ color: "#1890ff" }} />
                                        <Select
                                            value={selectedDevice}
                                            onChange={(value) => setSelectedDevice(value)}
                                            style={{ width: 200 }}
                                        >
                                            {devices.map((device) => (
                                                <Select.Option key={device.deviceId} value={device.deviceId}>
                                                    {device.label}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Space>
                                </div>
                            </Space>
                        </Col>

                        {/* Display Controls */}
                        <Col span={8}>
                            <h3 style={{ marginBottom: 8 }}>Display Controls</h3>
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <div>
                                    <label style={{ display: "block", marginBottom: 4 }}>Zoom Level</label>
                                    <Space align="center">
                                        <IconButton icon={<ZoomOut />} onClick={handleZoomOut} disabled={zoom === 1} />
                                        <Slider
                                            min={1}
                                            max={5}
                                            step={0.1}
                                            value={zoom}
                                            style={{ width: 200 }}
                                            onChange={(value) => {
                                                const newZoom = Number(value);
                                                setZoom(newZoom);
                                                if (newZoom === 1) setPosition({ x: 0, y: 0 });
                                            }}
                                        />
                                        <IconButton icon={<ZoomIn />} onClick={handleZoomIn} disabled={zoom === 5} />
                                        <span>{zoom.toFixed(1)}x</span>
                                    </Space>
                                </div>

                                {endoscopeMask && (
                                    <div>
                                        <label style={{ display: "block", marginBottom: 4 }}>Mask Size</label>
                                        <Space align="center">
                                            <Slider
                                                min={10}
                                                max={50}
                                                value={maskSize}
                                                style={{ width: 200 }}
                                                onChange={(value) => setMaskSize(Number(value))}
                                            />
                                            <span>{maskSize}%</span>
                                        </Space>
                                    </div>
                                )}
                            </Space>
                        </Col>

                        {/* Effects */}
                        <Col span={8}>
                            <h3 style={{ marginBottom: 8 }}>Effects</h3>
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <IconButton
                                    icon={endoscopeMask ? <EyeOff /> : <Eye />}
                                    onClick={() => setEndoscopeMask(!endoscopeMask)}
                                    position="right"
                                    style={{ gap: "5px", display: "flex", width: "100%" }}
                                    className="secondary border-grey flex"
                                >
                                    <span style={{ marginRight: "auto" }}>Endoscope Mask</span>
                                </IconButton>
                                <IconButton
                                    icon={handShake ? <HandOff /> : <Hand />}
                                    onClick={() => setHandShake(!handShake)}
                                    position="right"
                                    style={{ gap: "5px", display: "flex", width: "100%" }}
                                    className="secondary border-grey flex"
                                >
                                    <span style={{ marginRight: "auto" }}> Hand Shake Effect</span>
                                </IconButton>
                                <IconButton
                                    icon={<Fullscreen />}
                                    onClick={toggleFullscreen}
                                    position="right"
                                    style={{ gap: "5px", display: "flex", width: "100%" }}
                                    className="secondary border-grey flex"
                                >
                                    <span style={{ marginRight: "auto" }}> Fullscreen Mode</span>
                                </IconButton>
                            </Space>
                        </Col>
                    </Row>
                </div>
            )}

            {/* Main content area */}
            <div>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    {isFullscreen ? (
                        <IconButton icon={<Menu />} onClick={() => setIsMenuOpen(!isMenuOpen)} position="center" />
                    ) : (
                        <div style={{ width: 32 }} /> /* Spacer */
                    )}

                    {isCameraActive &&
                        (!isRecording ? (
                            <IconButton
                                icon={<Videocam />}
                                style={{ gap: "5px", display: "flex" }}
                                onClick={handleStartRecording}
                                position="left"
                                className="dark flex"
                            >
                                Record
                            </IconButton>
                        ) : (
                            <IconButton
                                icon={<Stop />}
                                style={{ gap: "5px", display: "flex" }}
                                onClick={handleStopRecording}
                                position="left"
                                className="secondary flex"
                            >
                                Stop
                            </IconButton>
                        ))}
                </Row>

                <div
                    style={{
                        position: "relative",
                        width: "100%",
                        overflow: "hidden",
                        borderRadius: 8,
                        cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onWheel={handleWheel}
                >
                    {endoscopeMask && (
                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                zIndex: 10,
                                background: "black",
                                pointerEvents: "none",
                                maskImage: `radial-gradient(circle at center, transparent ${maskSize}%, black ${
                                    maskSize + 1
                                }%)`,
                                WebkitMaskImage: `radial-gradient(circle at center, transparent ${maskSize}%, black ${
                                    maskSize + 1
                                }%)`,
                                animation: handShake ? "shake 0.5s infinite" : "none",
                            }}
                        />
                    )}
                    <div
                        ref={viewportRef}
                        style={{
                            position: "relative",
                            transition: "transform 100ms",
                            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                            transformOrigin: "center",
                        }}
                    >
                        {isCameraActive ? (
                            <Webcam
                                audio={true}
                                muted={true}
                                ref={webcamRef}
                                screenshotFormat="image/jpeg"
                                videoConstraints={videoConstraints}
                                style={{
                                    borderRadius: 8,
                                    width: "100%",
                                    maxHeight: isFullscreen ? "initial" : "calc(100vh - 380px)",
                                }}
                            />
                        ) : recordedChunks.length > 0 ? (
                            <video
                                src={URL.createObjectURL(new Blob(recordedChunks, { type: "video/webm" }))}
                                controls
                                style={{
                                    borderRadius: 8,
                                    width: "100%",
                                    maxHeight: isFullscreen ? "initial" : "calc(100vh - 400px)",
                                }}
                            />
                        ) : (
                            photo && (
                                <img
                                    src={photo}
                                    alt="Captured"
                                    style={{
                                        borderRadius: 8,
                                        width: "100%",
                                        maxHeight: isFullscreen ? "initial" : "calc(100vh - 400px)",
                                    }}
                                />
                            )
                        )}
                    </div>
                </div>

                <Row justify="center" style={{ marginTop: 16 }}>
                    <Space>
                        {isCameraActive ? null : (
                            <>
                                <IconButton
                                    icon={<Refresh />}
                                    style={{ gap: "5px", display: "flex" }}
                                    onClick={retake}
                                    position="left"
                                    className="secondary flex"
                                >
                                    Retake
                                </IconButton>
                                {recordedChunks.length > 0 && (
                                    <IconButton
                                        icon={<DownloadIcon />}
                                        style={{ gap: "5px", display: "flex" }}
                                        onClick={handleDownload}
                                        position="left"
                                        className="secondary flex"
                                    >
                                        Download
                                    </IconButton>
                                )}
                            </>
                        )}
                    </Space>
                </Row>
            </div>

            {/* Slide-out menu - only shown in fullscreen */}
            {isFullscreen && isMenuOpen && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        height: "100%",
                        width: 350,
                        background: "white",
                        boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                        transform: isMenuOpen ? "translateX(0)" : "translateX(-100%)",
                        transition: "transform 300ms ease-in-out",
                        zIndex: 50,
                        padding: 16,
                    }}
                >
                    <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                        <h3 style={{ margin: 0 }}>Controls</h3>
                        <IconButton icon={<X />} onClick={() => setIsMenuOpen(false)} />
                    </Row>

                    <Space direction="vertical" style={{ width: "100%" }}>
                        <div>
                            <label style={{ display: "block", marginBottom: 4 }}>Camera</label>
                            <Space>
                                <Devices style={{ color: "#1890ff" }} />
                                <Select
                                    value={selectedDevice}
                                    onChange={(value) => setSelectedDevice(value)}
                                    style={{ width: "100%" }}
                                >
                                    {devices.map((device) => (
                                        <Select.Option key={device.deviceId} value={device.deviceId}>
                                            {device.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Space>
                        </div>

                        {endoscopeMask && (
                            <div>
                                <label style={{ display: "block", marginBottom: 4 }}>Mask Size</label>
                                <Space align="center">
                                    <Slider
                                        min={10}
                                        max={50}
                                        value={maskSize}
                                        style={{ width: "100%" }}
                                        onChange={(value) => setMaskSize(Number(value))}
                                    />
                                    <span>{maskSize}%</span>
                                </Space>
                            </div>
                        )}

                        <div>
                            <label style={{ display: "block", marginBottom: 4 }}>Zoom</label>
                            <Space align="center">
                                <IconButton icon={<ZoomOut />} onClick={handleZoomOut} disabled={zoom === 1} />
                                <Slider
                                    min={1}
                                    max={5}
                                    step={0.1}
                                    value={zoom}
                                    style={{ width: 150 }}
                                    onChange={(value) => {
                                        const newZoom = Number(value);
                                        setZoom(newZoom);
                                        if (newZoom === 1) setPosition({ x: 0, y: 0 });
                                    }}
                                />
                                <IconButton icon={<ZoomIn />} onClick={handleZoomIn} disabled={zoom === 5} />
                                <span>{zoom.toFixed(1)}x</span>
                            </Space>
                        </div>

                        <div>
                            <label style={{ display: "block", marginBottom: 4 }}>Display Options</label>
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <IconButton
                                    icon={endoscopeMask ? <EyeOff /> : <Eye />}
                                    onClick={() => setEndoscopeMask(!endoscopeMask)}
                                    position="right"
                                    style={{ gap: "5px", display: "flex", width: "100%" }}
                                    className="secondary border-grey flex"
                                >
                                    <span style={{ marginRight: "auto" }}>Endoscope Mask</span>
                                </IconButton>
                                <IconButton
                                    icon={handShake ? <HandOff /> : <Hand />}
                                    onClick={() => setHandShake(!handShake)}
                                    position="right"
                                    style={{ gap: "5px", display: "flex", width: "100%" }}
                                    className="secondary border-grey flex"
                                >
                                    <span style={{ marginRight: "auto" }}>Hand Shake Effect</span>
                                </IconButton>
                                <IconButton
                                    icon={isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                                    onClick={toggleFullscreen}
                                    position="right"
                                    style={{ gap: "5px", display: "flex", width: "100%" }}
                                    className="secondary border-grey flex"
                                >
                                    <span style={{ marginRight: "auto" }}>Fullscreen</span>
                                </IconButton>
                            </Space>
                        </div>
                    </Space>
                </div>
            )}

            {/* Overlay when menu is open */}
            {isFullscreen && isMenuOpen && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.5)",
                        zIndex: 40,
                    }}
                    onClick={() => setIsMenuOpen(false)}
                />
            )}
        </Card>
    );
};

export default WebcamViewer;
